import { useEffect, useRef, useState } from 'react';

interface UseBarcodeScannerResult {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  error: string | null;
}

export const useBarcodeScanner = (onDetect: (value: string) => void): UseBarcodeScannerResult => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const detectedRef = useRef(false);

  useEffect(() => {
    let stream: MediaStream | null = null;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const startScanner = async (): Promise<void> => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });

        const video = videoRef.current;
        if (!video) return;

        video.srcObject = stream;
        await video.play();

        const detector = new (window as unknown as BarcodeDetectorWindow).BarcodeDetector({
          formats: ['code_128', 'code_39', 'qr_code', 'ean_13', 'ean_8', 'upc_a', 'upc_e'],
        });

        intervalId = setInterval(async () => {
          if (detectedRef.current || !video || video.readyState < 2) return;

          try {
            const barcodes = await detector.detect(video);
            if (barcodes.length > 0 && barcodes[0].rawValue) {
              detectedRef.current = true;
              onDetect(barcodes[0].rawValue);
            }
          } catch {
            // Ignore per-frame detection errors
          }
        }, 200);
      } catch (err) {
        const message =
          err instanceof Error && err.name === 'NotAllowedError'
            ? 'Camera permission denied. Please allow camera access and try again.'
            : 'Unable to access camera. Please use manual search instead.';
        setError(message);
      }
    };

    startScanner();

    return (): void => {
      if (intervalId) clearInterval(intervalId);
      if (stream)
        stream.getTracks().forEach((t) => {
          t.stop();
        });
    };
  }, [onDetect]);

  return { videoRef, error };
};

interface BarcodeDetectorResult {
  rawValue: string;
}

interface BarcodeDetectorWindow {
  BarcodeDetector: new (options?: { formats?: string[] }) => {
    detect: (source: HTMLVideoElement) => Promise<BarcodeDetectorResult[]>;
  };
}
